import { Component } from '@angular/core';
import { ReusableQuestionComponents } from '@app/shared/components/questions/reusable-question-components/reusable-question-components';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '@app/core/services/alert.service';
import { CoreService } from '@app/core/services/core.service';
import { Questionnaire, QuestionnaireSubmitPayload } from '@app/core/models/questionnaire.model';
import { extractResponse } from '@app/shared/utils/response.utils';
import { AlertMethod } from '@app/core/models/alert.model';
import { ApiStateWrapper } from '@app/shared/components/api-state-wrapper/api-state-wrapper';
import {
  QuestionType,
  QuestionConditionParameter,
  RedirectionOption,
} from '@app/core/enums/questionnaire.enums';
import {
  SurveyWelcomeScreen,
  SurveyHeader,
  SurveyPageSection,
  SurveyFooter,
  SurveyExitScreen,
} from '@app/features/common/components/survey-sections';
import { APP_ROUTES } from '@app/core/constants/routes.constants';

@Component({
  selector: 'app-qr-based',
  imports: [
    ReusableQuestionComponents,
    ApiStateWrapper,
    SurveyWelcomeScreen,
    SurveyHeader,
    SurveyPageSection,
    SurveyFooter,
    SurveyExitScreen,
  ],
  templateUrl: './qr-based.html',
  styleUrl: './qr-based.scss',
})
export class QrBased {
  organizationId: string = '';
  questionnaireId: string = '';
  token: string = '';

  isLoading = false;
  isError = false;
  errorMessage = '';
  errorDetails = '';
  questionnaire: Questionnaire | null = null;
  isSubmitting = false;
  isSuccess = false;
  isDuplicateResponse = false;
  welcomeScreenShown = false;
  currentPage = 1;
  /** When MANUAL redirection: true if submitted score matched RATING condition (show Google block on exit screen). */
  showReviewRedirection = false;
  APP_ROUTES = APP_ROUTES;

  constructor(
    private route: ActivatedRoute,
    private coreService: CoreService,
    private alertService: AlertService,
    private router: Router
  ) {
    this.route.params.subscribe((params) => {
      this.organizationId = params['organizationId'];
      this.questionnaireId = params['questionnaireId'];
      this.token = params['token'];
    });
    this.route.queryParams.subscribe((queryParams) => {
      this.isSuccess = queryParams['isSuccess'] === 'true';
      this.isDuplicateResponse = queryParams['isDuplicateResponse'] === 'true';
    });
    this.isSuccess = this.route.snapshot.queryParams['isSuccess'] === 'true';
    this.isDuplicateResponse =
      this.route.snapshot.queryParams['isDuplicateResponse'] === 'true';
  }

  ngOnInit(): void {
    this.getQRBasedQuestionnaire();
  }

  getQRBasedQuestionnaire(): void {
    this.isLoading = true;
    this.isError = false;
    this.errorMessage = '';
    this.errorDetails = '';
    this.coreService.getQRBasedQuestionnaire(this.organizationId, this.questionnaireId).subscribe({
      next: (result) => {
        if (result.success) {
          this.questionnaire = extractResponse(result);
          this.isError = false;
        } else {
          this.questionnaire = null;
          this.isError = true;
          this.errorMessage = result.message || 'Failed to fetch questionnaire details';
          this.errorDetails = result.details || 'Please try again later or contact support if the problem persists.';
        }
      },
      error: (error) => {
        console.error('Error in getQRBasedQuestionnaire:', error);
        this.questionnaire = null;
        this.errorMessage = error?.message || 'Failed to fetch questionnaire details';
        this.errorDetails = 'Please try again later or contact support if the problem persists.';
        this.isError = true;
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  onSubmitAnswers(payload: QuestionnaireSubmitPayload): void {
    if (Object.keys(payload).length === 0) {
      this.alertService.showWarning(
        'Warning',
        'Please fill all the required fields',
        AlertMethod.SOLID_BACKGROUND,
        5000
      );
      return;
    }
    this.isSubmitting = true;
    this.coreService.submitSummitResponse(payload).subscribe({
      next: (result) => {
        if (result.success) {
          this.isSuccess = true;
          this.tryOpenReviewUrlIfAutomatic(payload);
          this.showReviewRedirection = this.evaluateReviewConditionMatch(payload);
          this.router.navigate(
            [`survey/${this.organizationId}/${this.questionnaireId}/${this.token}`],
            { queryParams: { isSuccess: true }, replaceUrl: true }
          );
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        if (error.errorCode === 'DUPLICATE_RESPONSE') {
          this.isDuplicateResponse = true;
          this.router.navigate(
            [`survey/${this.organizationId}/${this.questionnaireId}/${this.token}`],
            { queryParams: { isDuplicateResponse: true }, replaceUrl: true }
          );
        } else {
          this.alertService.showError(
            'Error',
            error.message || 'Failed to submit response',
            AlertMethod.SOLID_BACKGROUND,
            5000
          );
        }
      },
      complete: () => {
        setTimeout(() => {
          this.isSubmitting = false;
        }, 9000);
      },
    });
  }

  refreshPage(): void {
    window.history.back();
  }

  refreshPreview(): void {
    this.getQRBasedQuestionnaire();
  }

  backToHome(): void {
    this.router.navigate(['/']);
  }

  onWelcomeStart(): void {
    this.welcomeScreenShown = true;
  }

  onCurrentPageChange(page: number): void {
    this.currentPage = page;
  }

  hasWelcomeScreen(): boolean {
    if (!this.questionnaire?.welcomeScreenTemplate) return false;
    const t = this.questionnaire.welcomeScreenTemplate;
    return !!(
      t.logo?.logoImage ||
      t.title?.text ||
      t.description?.text ||
      t.button?.name
    );
  }

  contactSupport(): void {
    this.router.navigate([APP_ROUTES.GUEST.SUPPORT.ROOT, APP_ROUTES.GUEST.SUPPORT.CONTACT_US]);
  }

  /**
   * Returns true if questionnaire has SOCIAL_REVIEW and the submitted score matches the RATING condition.
   * Used for AUTOMATIC (open tab) and MANUAL (show Google block on exit screen).
   */
  private evaluateReviewConditionMatch(payload: QuestionnaireSubmitPayload): boolean {
    const questionnaire = this.questionnaire;
    if (!questionnaire?.questions?.length || !payload?.responses?.length) return false;

    const socialQuestion = questionnaire.questions.find(
      (q) => q.questionType === QuestionType.SOCIAL_REVIEW
    );
    if (!socialQuestion?.questionSpecificOptions) return false;

    const response = payload.responses.find(
      (r: any) => r.questionId === socialQuestion.questionId && r.score != null
    );
    if (!response || typeof (response as any).score !== 'number') return false;

    const score = (response as any).score as number;
    const ratingCondition = socialQuestion.questionConditions?.find(
      (c) => c.parameter === QuestionConditionParameter.RATING || c.parameter === 'RATING'
    );
    if (!ratingCondition || typeof ratingCondition.value !== 'number') return false;

    const op = ratingCondition.operator;
    const val = ratingCondition.value;
    return (
      op === 'EQUALS'
        ? score === val
        : op === 'GREATER_THAN'
          ? score > val
          : op === 'GREATER_THAN_OR_EQUAL'
            ? score >= val
            : false
    );
  }

  /**
   * If questionnaire has SOCIAL_REVIEW with redirectionOption AUTOMATIC, evaluate RATING condition
   * against the submitted score and open reviewUrl in a new tab when the condition matches.
   */
  private tryOpenReviewUrlIfAutomatic(payload: QuestionnaireSubmitPayload): void {
    const questionnaire = this.questionnaire;
    if (!questionnaire?.questions?.length || !payload?.responses?.length) return;

    const socialQuestion = questionnaire.questions.find(
      (q) => q.questionType === QuestionType.SOCIAL_REVIEW
    );
    if (!socialQuestion?.questionSpecificOptions) return;

    const opts = socialQuestion.questionSpecificOptions;
    if (opts.redirectionOption !== RedirectionOption.AUTOMATIC || !opts.reviewUrl?.trim()) return;
    if (!this.evaluateReviewConditionMatch(payload)) return;

    window.open(opts.reviewUrl.trim(), '_blank', 'noopener,noreferrer');
  }
}
